import { useState, useRef, useCallback, useEffect } from 'react';
import {
  uploadImage,
  uploadVideo,
  uploadAudio,
  getUploadStatus,
  getMediaType,
  getAllowedTypes,
  getSizeLimit,
  formatFileSize,
  type MediaType,
  type UploadResult,
  type UploadCategory,
} from '@/api/upload';

interface FileUploadFieldProps {
  label: string;
  mediaType: MediaType;
  value?: UploadResult | null;
  onChange: (result: UploadResult | null) => void;
  category?: UploadCategory;
  required?: boolean;
  hint?: string;
  error?: string;
  showUrlFallback?: boolean;
  urlValue?: string;
  onUrlChange?: (url: string) => void;
}

type UploadState = 'idle' | 'uploading' | 'processing' | 'success' | 'error';

export function FileUploadField({
  label,
  mediaType,
  value,
  onChange,
  category = 'nostalgia',
  required,
  hint,
  error: externalError,
  showUrlFallback = true,
  urlValue = '',
  onUrlChange,
}: FileUploadFieldProps) {
  const [uploadState, setUploadState] = useState<UploadState>(value ? 'success' : 'idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [useUrl, setUseUrl] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const allowedTypes = getAllowedTypes(mediaType);
  const sizeLimit = getSizeLimit(mediaType);
  const acceptAttr = allowedTypes.join(',');

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const pollStatus = useCallback(async (uploadId: string) => {
    try {
      const status = await getUploadStatus(uploadId);
      if (status.status === 'ready') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setUploadState('success');
        setProgress(100);
        onChange(status);
      } else if (status.status === 'failed') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setUploadState('error');
        setError(status.processError || 'Processing failed');
      }
    } catch (err) {
      console.error('Failed to poll status:', err);
    }
  }, [onChange]);

  const handleUpload = async (file: File) => {
    // Validate file type
    const detectedType = getMediaType(file);
    if (detectedType !== mediaType) {
      setError(`Please select a valid ${mediaType} file`);
      return;
    }

    // Validate file size
    if (file.size > sizeLimit) {
      setError(`File must be less than ${formatFileSize(sizeLimit)}`);
      return;
    }

    setUploadState('uploading');
    setProgress(0);
    setError('');

    try {
      let result: UploadResult;

      // Simulate progress during upload
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      switch (mediaType) {
        case 'image':
          result = await uploadImage(file, category);
          break;
        case 'video':
          result = await uploadVideo(file, category);
          break;
        case 'audio':
          result = await uploadAudio(file, category);
          break;
      }

      clearInterval(progressInterval);

      // Check if processing is needed
      if (result.status === 'processing' || result.status === 'pending') {
        setUploadState('processing');
        setProgress(95);
        // Start polling for status
        pollIntervalRef.current = setInterval(() => pollStatus(result.id), 2000);
      } else if (result.status === 'failed') {
        setUploadState('error');
        setError(result.processError || 'Upload failed');
      } else {
        setUploadState('success');
        setProgress(100);
        onChange(result);
      }
    } catch (err) {
      setUploadState('error');
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleRemove = () => {
    onChange(null);
    setUploadState('idle');
    setProgress(0);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderPreview = () => {
    if (!value) return null;

    switch (mediaType) {
      case 'image':
        return (
          <img
            src={value.thumbnailUrl || value.url}
            alt="Preview"
            className="max-w-full max-h-48 rounded-md object-contain"
          />
        );
      case 'video':
        return (
          <div className="relative">
            {value.thumbnailUrl ? (
              <img
                src={value.thumbnailUrl}
                alt="Video thumbnail"
                className="max-w-full max-h-48 rounded-md object-contain"
              />
            ) : (
              <video
                src={value.url}
                className="max-w-full max-h-48 rounded-md"
                controls={false}
              />
            )}
            {value.duration && (
              <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
                {Math.floor(value.duration / 60)}:{(value.duration % 60).toString().padStart(2, '0')}
              </span>
            )}
          </div>
        );
      case 'audio':
        return (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Audio uploaded</p>
              {value.duration && (
                <p className="text-xs text-gray-500">
                  Duration: {Math.floor(value.duration / 60)}:{(value.duration % 60).toString().padStart(2, '0')}
                </p>
              )}
            </div>
          </div>
        );
    }
  };

  const displayError = externalError || error;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {showUrlFallback && (
          <button
            type="button"
            onClick={() => setUseUrl(!useUrl)}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            {useUrl ? 'Upload file' : 'Use URL instead'}
          </button>
        )}
      </div>

      {useUrl ? (
        <input
          type="url"
          value={urlValue}
          onChange={(e) => onUrlChange?.(e.target.value)}
          placeholder="https://..."
          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            displayError ? 'border-red-500' : 'border-gray-300'
          }`}
        />
      ) : (
        <>
          {/* Upload zone */}
          {uploadState === 'idle' && !value && (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : displayError
                  ? 'border-red-300 hover:border-red-400'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptAttr}
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="space-y-2">
                <div className="mx-auto w-12 h-12 text-gray-400">
                  {mediaType === 'image' && (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                  {mediaType === 'video' && (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                  {mediaType === 'audio' && (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  Max {formatFileSize(sizeLimit)}
                </p>
              </div>
            </div>
          )}

          {/* Uploading state */}
          {(uploadState === 'uploading' || uploadState === 'processing') && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">
                    {uploadState === 'uploading' ? 'Uploading...' : 'Processing...'}
                  </p>
                  <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success state with preview */}
          {uploadState === 'success' && value && (
            <div className="border rounded-lg p-3 bg-gray-50">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  {renderPreview()}
                </div>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="p-1 text-gray-400 hover:text-red-500"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {formatFileSize(value.size)}
                {value.width > 0 && ` • ${value.width}x${value.height}`}
              </p>
            </div>
          )}

          {/* Error state */}
          {uploadState === 'error' && (
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-700">Upload failed</p>
                  <p className="text-xs text-red-600">{error}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setUploadState('idle');
                    setError('');
                  }}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Try again
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {hint && !displayError && <p className="text-xs text-gray-500">{hint}</p>}
      {displayError && uploadState !== 'error' && (
        <p className="text-xs text-red-500">{displayError}</p>
      )}
    </div>
  );
}
